import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Starea accordion-ului din sidebar (grupurile Vânzări/Achiziții/Stocuri).
 *
 * De ce store, nu `useState` în sidebar: starea „grup deschis" TREBUIE să
 * supraviețuiască re-mount-ului sidebar-ului (declanșat de `refreshProfile()`
 * după restart-ul turului ghidat). Un store Zustand e modul-scoped → nu se
 * resetează la re-mount, spre deosebire de `useState`.
 *
 * `pinnedGroup` e fixat de turul ghidat și are prioritate peste orice
 * auto-colaps (reconcilierea pe pathname / close-all), ca highlight-ul turului
 * să nu pice pe alt item atunci când grupul s-ar colapsa.
 *
 * Accordion: cel mult UN grup deschis la un moment dat.
 */
interface SidebarGroupsStore {
  /** Grupul deschis manual (accordion). Persistat în localStorage. */
  openGroup: string | null;
  /** Grup fixat de tur — override peste auto-colaps. NU se persistă. */
  pinnedGroup: string | null;
  /** Toggle din header-ul grupului (acțiune user). */
  toggleGroup: (name: string) => void;
  /** Reconciliere la schimbarea rutei — deschide grupul rutei active (sau null). NO-OP cât timp e pin. */
  syncActiveGroup: (name: string | null) => void;
  /** Închide toate grupurile (click pe item flat). NO-OP cât timp e pin. */
  closeAllGroups: () => void;
  /** Fixează/dez-fixează un grup (turul ghidat). */
  pinGroup: (name: string | null) => void;
  /** Curăță openGroup dacă nu mai există în navigație (anti-stale, ex. grup redenumit/mutat). */
  validateAgainst: (validNames: string[]) => void;
}

export const useSidebarGroupsStore = create<SidebarGroupsStore>()(
  persist(
    (set) => ({
      openGroup: null,
      pinnedGroup: null,
      toggleGroup: (name) =>
        set((s) => ({ openGroup: s.openGroup === name ? null : name })),
      syncActiveGroup: (name) =>
        set((s) => (s.pinnedGroup || s.openGroup === name ? s : { openGroup: name })),
      closeAllGroups: () =>
        set((s) => (s.pinnedGroup || s.openGroup === null ? s : { openGroup: null })),
      pinGroup: (name) => set((s) => (s.pinnedGroup === name ? s : { pinnedGroup: name })),
      validateAgainst: (validNames) =>
        set((s) => (s.openGroup && !validNames.includes(s.openGroup) ? { openGroup: null } : s)),
    }),
    {
      name: 'sf-sidebar-open-group',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined'
          ? localStorage
          : ({ getItem: () => null, setItem: () => {}, removeItem: () => {} } as unknown as Storage),
      ),
      partialize: (s) => ({ openGroup: s.openGroup }),
    },
  ),
);

/** Selector helper: grupul care trebuie afișat ca deschis (pin-ul are prioritate). */
export const selectEffectiveOpenGroup = (s: SidebarGroupsStore): string | null =>
  s.pinnedGroup ?? s.openGroup;
